import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useLocale } from '@hooks/useLocale';
import {
  LOCALE_ONLY_PATH_PATTERN,
  withLocalePrefix,
} from '@utils/breadcrumbs/withLocalePrefix';
import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import SimpleLink from '../UI/SimpleLink';
import SimpleButton from '../UI/SimpleButton';
import styles from './Breadcrumbs.module.scss';

export type BreadcrumbItem = {
  id: string;
  label: string;
  href: string | null;
};

export type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  ariaLabel?: string;
  className?: string;
  productName?: string;
};

const Breadcrumbs = ({
  items,
  ariaLabel,
  className,
  productName,
}: BreadcrumbsProps) => {
  const { t } = useTranslation('common');
  const locale = useLocale();
  const isMobile = useBreakpoint(Breakpoints.md);
  const [expanded, setExpanded] = useState(false);
  const [exceedsSingleLine, setExceedsSingleLine] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);
  const normalizedItems = useMemo(() => {
    const homeItem = {
      id: 'breadcrumb-home',
      label: t('breadcrumb.home'),
      href: '/',
    };

    if (!items?.length) {
      return productName ? [homeItem] : [];
    }

    const firstItemHref = items[0]?.href ?? '';
    const firstItemIsHome =
      firstItemHref === '/' || LOCALE_ONLY_PATH_PATTERN.test(firstItemHref);

    return [homeItem, ...(firstItemIsHome ? items.slice(1) : items)];
  }, [items, productName, t]);

  const measureWrappedRows = useCallback(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const listItems = Array.from(listElement.querySelectorAll('li'));
    if (listItems.length <= 1) {
      setExceedsSingleLine(false);
      return;
    }

    const firstOffsetTop = listItems[0]?.offsetTop ?? 0;
    const hasMultipleRows = listItems.some(
      (listItem) => Math.abs(listItem.offsetTop - firstOffsetTop) > 1
    );

    setExceedsSingleLine(hasMultipleRows);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setExpanded(false);
      setExceedsSingleLine(false);
      return undefined;
    }

    if (expanded) {
      setExceedsSingleLine(false);
      return undefined;
    }

    const rafId = window.requestAnimationFrame(measureWrappedRows);
    measureWrappedRows();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureWrappedRows);

      return () => {
        window.cancelAnimationFrame(rafId);
        window.removeEventListener('resize', measureWrappedRows);
      };
    }

    const listElement = listRef.current;
    if (!listElement) {
      return () => window.cancelAnimationFrame(rafId);
    }

    const resizeObserver = new ResizeObserver(measureWrappedRows);
    resizeObserver.observe(listElement);

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [expanded, isMobile, measureWrappedRows, normalizedItems]);

  const shouldCollapse = isMobile && !expanded && exceedsSingleLine;
  const firstCollapsedLabel =
    normalizedItems[1]?.label ?? normalizedItems[0]?.label ?? '';

  const onExpand = () => {
    setExpanded(true);
  };

  if (!normalizedItems.length) {
    return null;
  }

  return (
    <nav
      className={cn(styles.container, className)}
      aria-label={ariaLabel ?? t('breadcrumb.aria')}
    >
      <ol
        className={cn(styles.list, {
          [styles.collapsedList]: shouldCollapse,
        })}
        ref={listRef}
      >
        {normalizedItems.map((item, index) => {
          const parentItemIndex = productName
            ? normalizedItems.length - 1
            : normalizedItems.length - 2;
          const isCurrentItem =
            !productName && index === normalizedItems.length - 1;
          const isParentOfCurrent = index === parentItemIndex;
          const isHiddenInCollapsedMobile =
            shouldCollapse && index < parentItemIndex;
          const href = item.href ? withLocalePrefix(item.href, locale) : '';

          return (
            <Fragment key={`${item.id}-wrapper`}>
              {shouldCollapse && isParentOfCurrent && (
                <li className={cn(styles.listItem, styles.ellipsisItem)}>
                  <SimpleButton
                    className={styles.ellipsisButton}
                    variant="secondary"
                    onClick={onExpand}
                    aria-label={t('breadcrumb.ellipsis.ariaLabel', {
                      label: firstCollapsedLabel,
                    })}
                  >
                    {t('breadcrumb.ellipsis')}
                  </SimpleButton>
                </li>
              )}
              <li
                key={item.id}
                className={cn(styles.listItem, {
                  [styles.collapsedHiddenItem]: isHiddenInCollapsedMobile,
                  [styles.parentItem]: shouldCollapse && isParentOfCurrent,
                  [styles.currentItem]:
                    isCurrentItem || (!productName && !item.href),
                })}
              >
                {isCurrentItem || !item.href ? (
                  <span className={styles.currentPage} aria-current="page">
                    {item.label}
                  </span>
                ) : (
                  <SimpleLink
                    href={href}
                    variant="secondary"
                    className={styles.link}
                    tabIndex={isHiddenInCollapsedMobile ? -1 : 0}
                  >
                    {item.label}
                  </SimpleLink>
                )}
              </li>
            </Fragment>
          );
        })}
        {productName && (
          <li className={cn(styles.listItem, styles.productNameItem)}>
            <span className={styles.lastItem} aria-current="page">
              {' '}
              {productName}{' '}
            </span>
          </li>
        )}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
